import { useState, useMemo } from "react";
import { AdminLayout } from "./AdminLayout";
import { Card, CardContent } from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { Badge } from "@/project/karuna/components/ui/badge"; // @ts-ignore - badge is not a valid module
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/project/karuna/components/ui/select"; // @ts-ignore - select is not a valid module
import {
  Trophy,
  Building2,
  FileText,
  Star,
  Filter,
  Loader2,
} from "lucide-react";
import { useToast } from "@/project/karuna/hooks/use-toast"; // @ts-ignore - use-toast is not a valid module
import { useQuery } from "@tanstack/react-query";
import {
  fetchSchoolProfileGridRecords,
  fetchSchemaGridRecordsAllPages,
  LARGE_GRID_FETCH,
} from "@/project/karuna/utils/fetchSchemaGridRecords";

const SUBMISSION_SCHEMA_NAME = "Activity Submission";
const SCHOOL_PROFILE_SCHEMA_NAME = "School Profile";

// Normalize mobile number helper
const normalizeMobileNumber = (mobileNumber: string): string => {
  return String(mobileNumber ?? "")
    .replace(/\D/g, "")
    .slice(0, 10);
};

interface SchoolRanking {
  schoolId: string;
  rank: number;
  schoolName: string;
  kcNo: string;
  chapter: string;
  avgScore: number;
  totalSubmissions: number;
  totalPoints: number;
}

const AdminSchoolRankings = () => {
  const { toast } = useToast();
  const [timeFilter, _setTimeFilter] = useState("all");
  const [chapterFilter, setChapterFilter] = useState("all");

  // Fetch all school profiles
  const { data: schoolProfilesData, isLoading: schoolsLoading } = useQuery({
    queryKey: ["school-profiles-rankings", SCHOOL_PROFILE_SCHEMA_NAME],
    queryFn: async () => {
      try {
        return await fetchSchoolProfileGridRecords();
      } catch (error) {
        console.error("Error fetching school profiles:", error);
        toast({
          title: "Error",
          description: "Failed to load school profiles",
          variant: "destructive",
        });
        return [];
      }
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // Fetch all submissions
  const { data: submissionsData, isLoading: submissionsLoading } = useQuery({
    queryKey: ["admin-submissions-rankings", SUBMISSION_SCHEMA_NAME],
    queryFn: async () => {
      try {
        return await fetchSchemaGridRecordsAllPages(
          SUBMISSION_SCHEMA_NAME,
          LARGE_GRID_FETCH
        );
      } catch (error) {
        console.error("Error fetching submissions:", error);
        toast({
          title: "Error",
          description: "Failed to load submissions",
          variant: "destructive",
        });
        return [];
      }
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // Process and rank schools
  const rankings = useMemo((): SchoolRanking[] => {
    if (
      !schoolProfilesData ||
      !submissionsData ||
      !Array.isArray(schoolProfilesData) ||
      !Array.isArray(submissionsData)
    ) {
      return [];
    }

    // Create a map of school contact to school profile
    const schoolMap = new Map<string, any>();
    schoolProfilesData.forEach((school: any) => {
      const contact = normalizeMobileNumber(
        school.contact ?? school.school_contact ?? school.schoolcontact ?? ""
      );
      if (contact) {
        schoolMap.set(contact, school);
      }
    });

    // Group submissions by school
    const schoolSubmissionsMap = new Map<string, any[]>();

    submissionsData.forEach((submission: any) => {
      const submissionContact = normalizeMobileNumber(
        submission.schoolcontact ??
          submission.contact ??
          submission.school_contact ??
          submission.basicInfo?.schoolcontact ??
          ""
      );

      if (submissionContact && schoolMap.has(submissionContact)) {
        const schoolId =
          schoolMap.get(submissionContact)?.id ??
          schoolMap.get(submissionContact)?._id;
        if (schoolId) {
          if (!schoolSubmissionsMap.has(schoolId)) {
            schoolSubmissionsMap.set(schoolId, []);
          }

          // Get score
          const score = submission.score ?? null;
          if (score !== null && score !== undefined) {
            // Apply time filter
            let includeSubmission = true;
            if (timeFilter !== "all") {
              const submittedAt =
                submission.submitted_at ??
                submission.created_on ??
                submission.created_at ??
                submission.submit_date ??
                "";

              if (submittedAt) {
                const submissionDate = new Date(submittedAt);
                if (!isNaN(submissionDate.getTime())) {
                  submissionDate.setHours(0, 0, 0, 0);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const startDate = new Date(today);
                  startDate.setHours(0, 0, 0, 0);

                  switch (timeFilter) {
                    case "week":
                      startDate.setDate(startDate.getDate() - 7);
                      break;
                    case "month":
                      startDate.setMonth(startDate.getMonth() - 1);
                      break;
                    case "quarter":
                      startDate.setMonth(startDate.getMonth() - 3);
                      break;
                    case "year":
                      startDate.setFullYear(startDate.getFullYear() - 1);
                      break;
                  }

                  includeSubmission =
                    submissionDate >= startDate && submissionDate <= today;
                } else {
                  includeSubmission = false;
                }
              } else {
                includeSubmission = false;
              }
            }

            if (includeSubmission) {
              schoolSubmissionsMap.get(schoolId)?.push({
                score: Number(score),
                submission,
              });
            }
          }
        }
      }
    });

    // Calculate rankings for each school
    const schoolRankings: SchoolRanking[] = [];

    schoolProfilesData.forEach((school: any) => {
      const schoolId = school.id ?? school._id;
      if (!schoolId) {
        return;
      }

      const submissions = schoolSubmissionsMap.get(schoolId) ?? [];

      // Only include schools with at least one submission
      if (submissions.length === 0) {
        return;
      }

      // Apply chapter filter (nearest karuna kendra)
      if (chapterFilter !== "all") {
        const schoolKendra =
          school.kendra ?? school.kendra_name ?? school.nearestkendra ?? "";
        if (
          String(schoolKendra).toLowerCase() !==
          String(chapterFilter).toLowerCase()
        ) {
          return;
        }
      }

      const totalPoints = submissions.reduce((sum, s) => sum + s.score, 0);
      const avgScore = Math.round(totalPoints / submissions.length);
      const totalSubmissions = submissions.length;

      const schoolName =
        school.schoolname ?? school.school_name ?? "Unknown School";
      const kcNo = school.kcno ?? school.kc_no ?? "N/A";
      const kendra =
        school.kendra ?? school.kendra_name ?? school.nearestkendra ?? "N/A";

      schoolRankings.push({
        schoolId,
        rank: 0, // Will be set after sorting
        schoolName,
        kcNo,
        chapter: kendra,
        avgScore,
        totalSubmissions,
        totalPoints,
      });
    });

    // Sort by average score descending, then by total submissions descending
    schoolRankings.sort((a, b) => {
      if (b.avgScore !== a.avgScore) {
        return b.avgScore - a.avgScore;
      }
      return b.totalSubmissions - a.totalSubmissions;
    });

    // Assign ranks
    schoolRankings.forEach((school, index) => {
      school.rank = index + 1;
    });

    return schoolRankings;
  }, [schoolProfilesData, submissionsData, timeFilter, chapterFilter]);

  // Calculate stats
  const stats = useMemo(() => {
    const participatingSchools = rankings.length;
    const totalSubmissions = rankings.reduce(
      (sum, s) => sum + s.totalSubmissions,
      0
    );
    const averageScore =
      participatingSchools > 0
        ? Math.round(
            rankings.reduce((sum, s) => sum + s.avgScore, 0) /
              participatingSchools
          )
        : 0;

    return { participatingSchools, totalSubmissions, averageScore };
  }, [rankings]);

  // Get unique karuna kendras for filter
  const chapters = useMemo(() => {
    if (!schoolProfilesData || !Array.isArray(schoolProfilesData)) {
      return [];
    }
    const kendraSet = new Set<string>();
    schoolProfilesData.forEach((school: any) => {
      const kendra =
        school.kendra ?? school.kendra_name ?? school.nearestkendra;
      if (kendra && kendra !== "N/A") {
        kendraSet.add(String(kendra));
      }
    });
    return Array.from(kendraSet).sort();
  }, [schoolProfilesData]);

  const getRankBadgeColor = (rank: number) => {
    if (rank === 1) {
      return "bg-yellow-500 text-white";
    }
    if (rank === 2) {
      return "bg-gray-400 text-white";
    }
    if (rank === 3) {
      return "bg-amber-700 text-white";
    }
    return "bg-gray-200 text-gray-700";
  };

  const getRankBorderClass = (rank: number) => {
    if (rank === 1) {
      return "border-2 border-gray-300";
    }
    if (rank === 2) {
      return "border-2 border-gray-300";
    }
    if (rank === 3) {
      return "border-2 border-gray-300";
    }
    return "border-border/50";
  };

  const getRankBadge = (rank: number) => {
    if (rank === 1) {
      return (
        <Badge className="border-0 bg-yellow-500 text-white">🥇 Champion</Badge>
      );
    }
    if (rank === 2) {
      return (
        <Badge className="border-0 bg-gray-400 text-white">🥈 Runner-up</Badge>
      );
    }
    if (rank === 3) {
      return (
        <Badge className="border-0 bg-amber-700 text-white">
          🥉 Third Place
        </Badge>
      );
    }
    return null;
  };

  const loading = schoolsLoading || submissionsLoading;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Trophy className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              School Rankings
            </h1>
            <p className="mt-1 text-muted-foreground">
              Schools ranked by average submission scores - aggregated
              performance view
            </p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Card className="border-border/50 shadow-medium">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-500/10">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="mb-1 text-sm font-medium text-muted-foreground">
                    Participating Schools
                  </h3>
                  <p className="text-3xl font-bold text-foreground">
                    {stats.participatingSchools}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50 shadow-medium">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-500/10">
                  <FileText className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="mb-1 text-sm font-medium text-muted-foreground">
                    Total Submissions
                  </h3>
                  <p className="text-3xl font-bold text-foreground">
                    {stats.totalSubmissions}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50 shadow-medium">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-yellow-500/10">
                  <Star className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <h3 className="mb-1 text-sm font-medium text-muted-foreground">
                    Average Score
                  </h3>
                  <p className="text-3xl font-bold text-foreground">
                    {stats.averageScore}/100
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters Section */}
        <Card className="border-border/50 bg-muted/30">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filters</span>
              </div>

              <Select value={chapterFilter} onValueChange={setChapterFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Karuna Kendra" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Karuna Kendras</SelectItem>
                  {chapters.map(kendra => (
                    <SelectItem key={kendra} value={kendra}>
                      {kendra}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Rankings List */}
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : rankings.length > 0 ? (
          <div className="space-y-4">
            {rankings.map(school => (
              <Card
                key={school.schoolId}
                className={`${getRankBorderClass(school.rank)} shadow-medium transition-all hover:shadow-lg`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-6">
                      {/* Rank Circle */}
                      <div
                        className={`flex h-12 w-12 items-center justify-center rounded-full text-lg font-bold ${getRankBadgeColor(
                          school.rank
                        )}`}
                      >
                        {school.rank}
                      </div>

                      {/* School Info */}
                      <div className="flex-1">
                        <div className="mb-2">
                          <h3 className="text-xl font-bold text-foreground">
                            {school.schoolName}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            KC No: {school.kcNo} • {school.chapter}
                          </p>
                        </div>

                        {/* Performance Metrics */}
                        <div className="flex flex-wrap items-center gap-6 text-sm">
                          <div>
                            <span className="text-2xl font-semibold text-foreground text-purple-800">
                              {school.avgScore}
                            </span>
                            <span className="text-muted-foreground">
                              {" "}
                              Score
                            </span>
                          </div>
                          <div>
                            <span className="text-2xl font-semibold text-blue-500 text-foreground">
                              {school.totalSubmissions}
                            </span>
                            <span className="text-muted-foreground">
                              {" "}
                              Submissions
                            </span>
                          </div>
                          <div>
                            <span className="text-2xl font-semibold text-foreground text-green-500">
                              {school.totalPoints}
                            </span>
                            <span className="text-muted-foreground">
                              {" "}
                              Total Points
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Badge for top 3 */}
                    <div className="flex-shrink-0">
                      {getRankBadge(school.rank)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">
                No schools found matching the selected filters.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSchoolRankings;
